import type { NonNullRef } from '@lemonade-hq/cdk';
import type { Milliseconds } from '@lemonade-hq/ts-helpers';
import type { UniclientAction, UniclientToast } from '@lemonade-hq/uniclient-chat';
import { useResponsiveValue, useToast } from '@lemonade-hq/uniclient-theme';
import { useCallback, useEffect, useRef } from 'react';
import { MOBILE_HEADER_SAFE_AREA } from '../elements/layout';
import { TOAST_MARGIN_BLOCK_START } from '../elements/Wizard.css';

interface UseWizardToastProps {
  readonly toast?: UniclientToast | UniclientToast[];
  readonly performActionRef?: NonNullRef<(action?: UniclientAction | undefined) => Promise<unknown>>;
  readonly currentStepId?: string;
  readonly animationDelay?: number;
}

const DEFAULT_ANIMATION_DELAY: Milliseconds = 800;

export const useWizardToast = ({
  toast,
  performActionRef,
  currentStepId,
  animationDelay = 0,
}: UseWizardToastProps = {}): void => {
  const toastMarginBlockStart = useResponsiveValue(TOAST_MARGIN_BLOCK_START);
  const { open, close, closeAll, setStyle } = useToast();
  const lastStepAttemptedShowingToastRef = useRef<string | null>(null);

  useEffect(() => {
    setStyle({
      marginBlockStart: `calc(${MOBILE_HEADER_SAFE_AREA}px + ${toastMarginBlockStart})`,
    });
  }, [setStyle, toastMarginBlockStart]);

  const handleAction = useCallback(
    (action: UniclientToast['action'], toastId?: string) => {
      switch (action?.type) {
        case 'dismiss':
          close(toastId);
          break;

        case 'refresh':
          window.location.reload();
          break;

        default:
          return void performActionRef?.current(action);
      }
    },
    [performActionRef, close],
  );

  useEffect(() => {
    const isNewStep = lastStepAttemptedShowingToastRef.current !== currentStepId;

    if (toast == null || isNewStep) {
      closeAll();

      if (toast == null) {
        lastStepAttemptedShowingToastRef.current = currentStepId ?? null;
        return;
      }
    }

    const toastArray = Array.isArray(toast) ? toast : [toast];
    const timeout = setTimeout(
      () => {
        open(
          toastArray.map(singleToast => ({
            ...singleToast,
            action:
              singleToast.action?.content != null
                ? {
                    onAction: () => handleAction(singleToast.action, singleToast.id),
                    label: singleToast.action.content,
                  }
                : undefined,
          })),
        );
        lastStepAttemptedShowingToastRef.current = currentStepId ?? null;
      },
      isNewStep ? animationDelay + DEFAULT_ANIMATION_DELAY : 0,
    );

    return () => clearTimeout(timeout);
  }, [open, toast, handleAction, closeAll, currentStepId, animationDelay]);
};
